import { Input } from "@aurumiq/ui/components/input";
import { Label } from "@aurumiq/ui/components/label";
import { Textarea } from "@aurumiq/ui/components/textarea";
import { cn } from "@aurumiq/ui/lib/utils";
import type * as React from "react";
import { useId } from "react";

export interface FieldProps {
  label: React.ReactNode;
  error?: string;
  hint?: React.ReactNode;
  className?: string;
  htmlFor?: string;
  children: React.ReactNode;
}

/** Label + control + error/hint stack used by every admin form. */
export function Field({ label, error, hint, className, htmlFor, children }: FieldProps) {
  return (
    <div className={cn("min-w-0", className)}>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error ? (
        <p className="mt-1 text-destructive text-xs">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-muted-foreground text-xs">{hint}</p>
      ) : null}
    </div>
  );
}

type ControlProps = Omit<FieldProps, "children" | "htmlFor">;

export function TextField({
  label,
  error,
  hint,
  className,
  id,
  ref,
  ...props
}: ControlProps & React.ComponentProps<"input">) {
  const auto = useId();
  const inputId = id ?? auto;
  return (
    <Field label={label} error={error} hint={hint} className={className} htmlFor={inputId}>
      <Input id={inputId} ref={ref} aria-invalid={error ? true : undefined} {...props} />
    </Field>
  );
}

export function TextAreaField({
  label,
  error,
  hint,
  className,
  id,
  ref,
  ...props
}: ControlProps & React.ComponentProps<"textarea">) {
  const auto = useId();
  const inputId = id ?? auto;
  return (
    <Field label={label} error={error} hint={hint} className={className} htmlFor={inputId}>
      <Textarea id={inputId} ref={ref} aria-invalid={error ? true : undefined} {...props} />
    </Field>
  );
}

/** Styled native `<select>`: works with react-hook-form `register` and is keyboard/test friendly. */
export function NativeSelect({ className, ref, ...props }: React.ComponentProps<"select">) {
  return (
    <select
      ref={ref}
      data-slot="native-select"
      className={cn(
        "flex h-9 w-full min-w-0 rounded-lg border border-input bg-card px-2.5 py-1.5 text-sm text-foreground outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive",
        className,
      )}
      {...props}
    />
  );
}

export function SelectField({
  label,
  error,
  hint,
  className,
  id,
  children,
  ...props
}: ControlProps & React.ComponentProps<"select">) {
  const auto = useId();
  const inputId = id ?? auto;
  return (
    <Field label={label} error={error} hint={hint} className={className} htmlFor={inputId}>
      <NativeSelect id={inputId} aria-invalid={error ? true : undefined} {...props}>
        {children}
      </NativeSelect>
    </Field>
  );
}
